/* eslint-disable indent */
import { DragEvent, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import Modal from '@/components/Modal';
import { WarningMessage } from '@/components/Message/WarningMessage';
import { TargetIcon } from '@/components/SvgIcons/TargetIcon';
import { useGetGroupsSelectOptions } from '@/hooks/group.hooks';
import { useGetCompaniesSelectOptions } from '@/hooks/company.hooks';
import { hideModal } from '@/utils/modal.utils';
import { mapVehicleCheckRequestsImport } from '@/utils/map.utils';
import { downloadTemplateVehiculeCheckRequestImport } from '@/utils/file.utils';
import { Select } from '@/components/Controls/Select';
import { TFunction } from 'i18next';
import {
  IconFileTypeCsv,
  IconFileSpreadsheet,
  IconExclamationCircleFilled,
} from '@tabler/icons-react';
import { Trans } from 'react-i18next';
import Loader from '@/components/Loading/Loader';
import { twMerge } from 'tailwind-merge';
import { useGetBranchesSelectOptions } from '@/hooks/branch.hooks';
import { useGetVehiculeCheckTypesSelectOptions } from '@/hooks/vehicule-check-type.hooks';
import { ActionUserType } from '@/api/entities/vehicule-check';
import { useImportVehiculeCheckRequests } from '@/hooks/vehicule-check.hooks';
import { VehiculeCheckImportItem } from '@/api/vehicule-check/vehicule-check';

interface VehiculeCheckRequestImportModalProps {
  t: TFunction<undefined>;
  id?: string;
}

export const VehiculeCheckRequestImportModal = ({
  t,
  id,
}: VehiculeCheckRequestImportModalProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [groupId, setGroupId] = useState<string>();
  const [companyId, setCompanyId] = useState<string>();
  const [branchId, setBranchId] = useState<string>();
  const [vehiculeCheckTypeId, setVehiculeCheckTypeId] = useState<string>();
  const [errorMessages, setErrorMessages] = useState<string[]>([]);
  const [parsedItems, setParsedItems] = useState<
    VehiculeCheckImportItem[] | null
  >(null);

  const { mutate: importCheckRequests } = useImportVehiculeCheckRequests({
    t,
    handleSuccess: () => {
      handleClearAll();
    },
  });

  const { getGroupsSelectOptions, isLoadingAllGroups } =
    useGetGroupsSelectOptions();

  const { getCompaniesSelectOptions, isLoadingAllCompanies } =
    useGetCompaniesSelectOptions({
      groupId,
    });

  const { getBranchesSelectOptions, isLoadingAllBranches } =
    useGetBranchesSelectOptions({
      groupId,
      companyId,
    });

  const { getVehiculeCheckTypesSelectOptions, isLoadingAllVehiculeCheckTypes } =
    useGetVehiculeCheckTypesSelectOptions({
      groupId,
      personTypes: [ActionUserType.INDIVIDUAL],
    });

  const groupListExists = getGroupsSelectOptions();
  const groupRequired = groupListExists;

  const companyListExists = getCompaniesSelectOptions();
  const companyRequired = !groupListExists && companyListExists;

  const branchListExists = getBranchesSelectOptions();
  const branchRequired =
    !groupListExists && !companyListExists && branchListExists;

  const isLoadingAll =
    isLoadingAllGroups ||
    isLoadingAllCompanies ||
    isLoadingAllBranches ||
    isLoadingAllVehiculeCheckTypes;

  const getInstructionMessage = () => {
    switch (true) {
      case Boolean(groupRequired) && !companyListExists && !branchListExists:
        return t('groupWithMissingCompanyAndBranchMessage');
      case Boolean(groupRequired) && !companyListExists:
        return t('groupWithMissingCompanyMessage');
      case Boolean(groupRequired) && !branchRequired:
        return t('groupWithMissingBranchMessage');
      case Boolean(groupRequired):
        return t('selectGroupCompanyBranchAndInspectionType');
      case Boolean(companyRequired) && !branchListExists:
        return t('companyWithMissingBranchMessage');
      case Boolean(companyRequired):
        return t('selectCompanyAndBranchAndInspectionType');
      case Boolean(branchRequired):
        return t('selectBranchAndInspectionType');
      default:
        return '';
    }
  };

  const ACCEPTED_EXTENSIONS = ['.csv', '.xlsx', '.xls'];

  const handleFile = (file: File | null) => {
    setFileName(file ? file.name : null);
    setErrorMessages([]);
    setParsedItems(null);

    if (!file) return;

    const isValidFormat = ACCEPTED_EXTENSIONS.some((ext) =>
      file.name.toLowerCase().endsWith(ext),
    );
    if (!isValidFormat) {
      setErrorMessages([`${t('invalidFileFormat')}.`]);
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const data = e.target?.result;
      const workbook = XLSX.read(data, { type: 'array' });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet) as object[];

      const rawRows = XLSX.utils.sheet_to_json<unknown[]>(worksheet, {
        header: 1,
      });
      const headerLength = rawRows[0]?.length ?? 0;
      const hasColumnMismatch = rawRows
        .slice(1)
        .some((row) => row.length > headerLength);

      const {
        items,
        hasInvalidDate,
        hasMissingVehicleIdentifier,
        hasMissingFirstName,
        hasMissingLastName,
        hasMissingEmail,
        hasInvalidEmail,
      } = mapVehicleCheckRequestsImport(jsonData);

      const errors: string[] = [];
      if (hasColumnMismatch) {
        errors.push(
          `${t('common:columnCountMismatch')}. ${t('common:pleaseCorrectYourFile')}`,
        );
      }
      if (hasMissingVehicleIdentifier) {
        errors.push(
          `${t('common:vinOrLicensePlateNumberIsRequired')}. ${t('common:pleaseCorrectYourFile')}`,
        );
      }
      if (hasMissingFirstName) {
        errors.push(
          `${t('common:firstnameRequired')}. ${t('common:pleaseCorrectYourFile')}`,
        );
      }
      if (hasMissingLastName) {
        errors.push(
          `${t('common:lastnameRequired')}. ${t('common:pleaseCorrectYourFile')}`,
        );
      }
      if (hasMissingEmail) {
        errors.push(
          `${t('common:emailRequired')}. ${t('common:pleaseCorrectYourFile')}`,
        );
      }
      if (hasInvalidEmail) {
        errors.push(
          `${t('common:invalidEmail')}. ${t('common:pleaseCorrectYourFile')}`,
        );
      }
      if (hasInvalidDate) {
        errors.push(
          `${t('common:inspectionStartDateIsInvalid')}. ${t('common:pleaseCorrectYourFile')}`,
        );
      }
      setErrorMessages(errors);
      if (errors.length === 0) {
        setParsedItems(items);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    handleFile(file);
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files?.[0] || null;
    handleFile(file);
  };

  const handleClickBrowse = () => {
    fileInputRef.current?.click();
  };

  const handleConfirm = () => {
    if (!parsedItems || errorMessages.length > 0) return;

    const itemsWithContext: VehiculeCheckImportItem[] = parsedItems.map(
      (item) => ({
        ...item,
        groupId: groupId || undefined,
        companyId: companyId || undefined,
        branchId: branchId || undefined,
        vehiculeCheckTypeId: vehiculeCheckTypeId || undefined,
      }),
    );

    importCheckRequests(itemsWithContext);
    hideModal(`#${id}`);
  };

  const handleClearAll = () => {
    setFileName(null);
    setErrorMessages([]);
    setParsedItems(null);
    setGroupId('');
    setCompanyId('');
    setBranchId('');
    setVehiculeCheckTypeId('');
  };

  const isSubmitDisabled =
    !parsedItems ||
    errorMessages.length > 0 ||
    !vehiculeCheckTypeId ||
    (!!groupRequired && !groupId) ||
    (!!companyRequired && !companyId) ||
    (!!branchRequired && !branchId);

  return (
    <Modal
      id={id}
      title={t('importInspectionRequests')}
      size="medium"
      showSecondBtn
      secondBtnLabel={t('common:confirm')}
      containerClassName="top-6"
      bodyClassName="px-6 py-5 m-0"
      disabledSubmitButton={isSubmitDisabled}
      onClickSecondBtn={handleConfirm}
      onClose={handleClearAll}
      onClickCancelBtn={handleClearAll}
      onClickOutsideContent={handleClearAll}
      onEscapePress={handleClearAll}
    >
      <div className="flex flex-col border border-gray-E5E5E5 px-5 pt-4 pb-6 rounded-lg mb-7.5">
        <div className="text-gray-700 text-md mb-5">
          <span className="font-semibold py-0.5 pl-1 pr-1.5 bg-[var(--purple-custom)] text-white text-sm rounded mr-1.5">
            1
          </span>
          <span className="text-black">
            <Trans
              i18nKey={getInstructionMessage()}
              components={{ span: <span className="text-gray-600" /> }}
            />
          </span>
          &nbsp;
          <span className="ml-1.5 text-black font-semibold">:</span>
        </div>
        <div className="grid grid-cols-3 gap-x-3 gap-y-4">
          {groupListExists && (
            <div>
              <div className="text-2sm font-medium mb-1">
                <span>{t('common:group')}</span>
                {groupRequired && (
                  <span className="ml-1 text-red-FF2929 text-md">*</span>
                )}
                <span className="ml-1">:</span>
              </div>
              <Select
                selectClassName="w-full"
                placeholder={t('common:chooseTheGroup')}
                value={groupId}
                onChange={(e) => setGroupId(e.target.value)}
                options={getGroupsSelectOptions()}
              />
            </div>
          )}
          {companyListExists && (
            <div>
              <div className="text-2sm font-medium mb-1">
                <span>{t('common:company')}</span>
                {companyRequired && (
                  <span className="ml-1 text-red-FF2929 text-md">*</span>
                )}
                <span className="ml-1">:</span>
              </div>
              <Select
                selectClassName="w-full"
                placeholder={t('common:chooseTheCompany')}
                value={companyId}
                onChange={(e) => setCompanyId(e.target.value)}
                options={getCompaniesSelectOptions()}
              />
            </div>
          )}
          {branchListExists && (
            <div>
              <div className="text-2sm font-medium mb-1">
                <span>{t('common:branch')}</span>
                {branchRequired && (
                  <span className="ml-1 text-red-FF2929 text-md">*</span>
                )}
                <span className="ml-1">:</span>
              </div>
              <Select
                selectClassName="w-full"
                placeholder={t('common:chooseTheBranch')}
                value={branchId}
                onChange={(e) => setBranchId(e.target.value)}
                options={getBranchesSelectOptions()}
              />
            </div>
          )}
          <div>
            <div className="text-2sm font-medium mb-1">
              <span>{t('common:inspectionType')}</span>
              <span className="ml-1 text-red-FF2929 text-md">*</span>
              <span className="ml-1">:</span>
            </div>
            <Select
              selectClassName="w-full"
              placeholder={t('common:chooseTheInspectionType')}
              value={vehiculeCheckTypeId}
              onChange={(e) => setVehiculeCheckTypeId(e.target.value)}
              options={getVehiculeCheckTypesSelectOptions()}
            />
          </div>
        </div>
      </div>

      <div className="ml-5 flex items-center text-black text-md mb-5">
        <span className="font-semibold py-[1px] px-1 bg-[var(--purple-custom)] text-white text-sm rounded mr-1.75">
          2
        </span>
        <div>
          <span>
            <Trans
              i18nKey={t('common:importCSVOrExcelFile')}
              components={{
                span: <span className="text-primary font-bold" />,
              }}
            />
          </span>
          &nbsp;
          <span
            className="text-pink-F20089 font-medium hover:cursor-pointer hover:underline"
            onClick={downloadTemplateVehiculeCheckRequestImport}
          >
            {t('common:byClickingHere')?.toLowerCase()}
          </span>
        </div>
      </div>

      <div
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        className="w-full flex flex-col justify-center items-center border-2 border-gray-300 border-dashed rounded-lg"
      >
        {fileName ? (
          <div className="p-8 text-center">
            {fileName.match(/\.csv$/i) ? (
              <IconFileTypeCsv
                stroke={2}
                className="w-10 h-10 text-primary mx-auto"
              />
            ) : fileName.match(/\.xls$/i) || fileName.match(/\.xlsx$/i) ? (
              <IconFileSpreadsheet
                stroke={2}
                className="w-10 h-10 text-primary mx-auto"
              />
            ) : (
              <IconExclamationCircleFilled
                stroke={2}
                className="w-10 h-10 text-red-FF2929 mx-auto"
              />
            )}
            <div className="mt-3 text-gray-1E1E1E font-medium">{fileName}</div>
            <div
              className="mt-3 px-3.5 py-1.5 border border-black/20 rounded-md text-gray-1E1E1E text-sm hover:cursor-pointer inline-block"
              onClick={handleClickBrowse}
            >
              {t('common:chooseAnotherFile')}
            </div>
          </div>
        ) : (
          <div className="p-8">
            <div className="flex justify-center items-center">
              <TargetIcon className="w-10 h-10 [&>path]:fill-gray-1E1E1E" />
            </div>
            <div className="mt-3 flex justify-center text-gray-1E1E1E text-xl font-medium">
              {t('common:dragAndDropFile')}
            </div>
            <div className="mt-3 flex justify-center text-gray-1E1E1E text-sm uppercase">
              {t('common:or')}
            </div>
            <div
              className="mt-3 px-3.5 py-1.5 border border-black/20 rounded-md text-gray-1E1E1E text-sm hover:cursor-pointer text-center"
              onClick={handleClickBrowse}
            >
              {t('common:searchFolderAndUploadFile')}
            </div>
            <div className="mt-3 text-gray-600 text-2sm text-center">
              {`${t('common:allowedFormats')} : CSV, XLSX, XLS`}
            </div>
          </div>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_EXTENSIONS.join(',')}
          className="hidden"
          onChange={handleInputChange}
        />
      </div>
      <div
        className={twMerge('space-y-2', errorMessages?.length > 0 && 'mt-3')}
      >
        {errorMessages?.map((msg, index) => (
          <WarningMessage
            key={index}
            containerClassName="pt-3 pb-2 w-full max-w-none justify-start mt-0 mb-0"
            textClassName="mt-[1px]"
            message={msg}
          />
        ))}
      </div>
      {isLoadingAll && <Loader />}
    </Modal>
  );
};
